// src/features/portfolio-optimization-tools/my-strategies/components/performanceMath.ts
//
// Shared performance math for the tear-sheet charts. Both the rebased
// "growth of 100" overlay and the monthly-alpha bar chart consume these helpers
// so their numbers are guaranteed to reconcile.
//
// The core idea is a TIME-WEIGHTED rebasing that strips out injected cash flows.
// The saved equity curve is a net-liquidating-value series that also absorbs
// periodic contributions, so a naive value/value ratio would conflate deposits
// with investment skill. Removing the daily cash flow before compounding yields
// a pure, contribution-neutral performance index that is directly comparable to
// a benchmark re-simulated on the same contribution schedule.

import type { EquityCurvePoint } from '../../multi-asset-portfolio-optimizer/api';

/** Every rebased series starts at this common base so both lines share a scale. */
export const REBASE_BASE = 100;

export interface RebasedPoint {
    /** ISO date string. */
    date: string;
    /** Time-weighted index, starting at REBASE_BASE. */
    index: number;
    /** True once live out-of-sample tracking has begun. */
    isLive: boolean;
}

type CurvePoint = EquityCurvePoint & { is_out_of_sample?: boolean };

/**
 * Rebase a net-liquidating-value equity curve to a time-weighted index that
 * starts at {@link REBASE_BASE} and strips injected cash flows.
 *
 * Daily time-weighted return: r_t = (V_t - CF_t) / V_{t-1} - 1, where CF_t is
 * the cash injected on day t. Compounding (1 + r_t) yields the index.
 */
export const rebaseToTWRR = (curve: CurvePoint[]): RebasedPoint[] => {
    if (curve.length === 0) return [];
    let index = REBASE_BASE;
    const out: RebasedPoint[] = [
        {
            date: curve[0].date,
            index,
            isLive: Boolean(curve[0].is_out_of_sample),
        },
    ];
    for (let i = 1; i < curve.length; i += 1) {
        const prevVal = curve[i - 1].portfolio_value;
        const cashFlow = curve[i].cash_flows_injected ?? 0;
        const val = curve[i].portfolio_value;
        const r = prevVal > 0 ? (val - cashFlow) / prevVal - 1 : 0;
        index *= 1 + r;
        out.push({
            date: curve[i].date,
            index,
            isLive: Boolean(curve[i].is_out_of_sample),
        });
    }
    return out;
};

export interface PeriodExcess {
    /** Sortable bucket key, 'YYYY-MM'. */
    period: string;
    /** Human label, e.g. "Jan 23". */
    label: string;
    /** Strategy time-weighted return for the period (fraction). */
    portReturn: number;
    /** Benchmark time-weighted return for the period (fraction). */
    benchReturn: number;
    /** Excess (active) return = portReturn - benchReturn (fraction). */
    excess: number;
    /** True if any day in the bucket was live out-of-sample. */
    isLive: boolean;
    /**
     * True when this bucket is a synthetic month appended purely so the X-axis
     * always reaches the current calendar month. It carries no real return data
     * and must NOT be rendered as a bar or shown in the tooltip.
     */
    isPlaceholder?: boolean;
}

/**
 * Bucket two aligned rebased index series into per-calendar-month time-weighted
 * returns and compute the monthly excess (active) return of the strategy over
 * the benchmark. The two input series MUST be date-aligned index-for-index
 * (the backend guarantees this for the compare endpoint).
 */
export const computeMonthlyExcessReturns = (
    port: RebasedPoint[],
    bench: RebasedPoint[],
): PeriodExcess[] => {
    const n = Math.min(port.length, bench.length);
    if (n === 0) return [];

    const order: string[] = [];
    const portEnd: Record<string, number> = {};
    const benchEnd: Record<string, number> = {};
    const liveMonth: Record<string, boolean> = {};

    for (let i = 0; i < n; i += 1) {
        const d = new Date(port[i].date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!(key in portEnd)) order.push(key);
        // Last write in a month wins -> month-end snapshot.
        portEnd[key] = port[i].index;
        benchEnd[key] = bench[i].index;
        liveMonth[key] = Boolean(liveMonth[key]) || port[i].isLive;
    }

    const result: PeriodExcess[] = [];
    let prevPort = REBASE_BASE;
    let prevBench = REBASE_BASE;
    order.forEach((key) => {
        const pv = portEnd[key];
        const bv = benchEnd[key];
        const portReturn = prevPort > 0 ? pv / prevPort - 1 : 0;
        const benchReturn = prevBench > 0 ? bv / prevBench - 1 : 0;
        const [year, month] = key.split('-');
        const label = new Date(
            Number(year),
            Number(month) - 1,
            1,
        ).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        result.push({
            period: key,
            label,
            portReturn,
            benchReturn,
            excess: portReturn - benchReturn,
            isLive: liveMonth[key],
        });
        prevPort = pv;
        prevBench = bv;
    });
    return result;
};

export interface WealthPoint {
    /** ISO date string. */
    date: string;
    /** Strategy wealth on this day, projected onto the initial investment. */
    strategyWealth: number;
    /** Benchmark wealth on this day, projected onto the initial investment. */
    benchmarkWealth: number;
    /** Absolute dollar gap (strategy minus benchmark) generated by alpha. */
    wealthDifference: number;
    /** True once live out-of-sample tracking has begun. */
    isLive: boolean;
}

/**
 * Projects the time-weighted index onto an actual starting investment amount,
 * showing the absolute dollar difference generated by the strategy's alpha.
 *
 * Formula: W_t = Investment * (Index_t / REBASE_BASE). Because both series
 * start at {@link REBASE_BASE}, they share the same initial wealth and the
 * widening dollar gap isolates compounding alpha. The two inputs MUST be
 * date-aligned index-for-index (guaranteed by the compare endpoint).
 */
export const computeWealthGeneration = (
    port: RebasedPoint[],
    bench: RebasedPoint[],
    initialInvestment: number,
): WealthPoint[] => {
    const n = Math.min(port.length, bench.length);
    if (n === 0) return [];

    const result: WealthPoint[] = [];
    for (let i = 0; i < n; i += 1) {
        const strategyWealth = initialInvestment * (port[i].index / REBASE_BASE);
        const benchmarkWealth =
            initialInvestment * (bench[i].index / REBASE_BASE);
        result.push({
            date: port[i].date,
            strategyWealth,
            benchmarkWealth,
            wealthDifference: strategyWealth - benchmarkWealth,
            isLive: port[i].isLive,
        });
    }
    return result;
};
